import React from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Accordion,
  Form,
  InputGroup,
  ListGroup,
} from "react-bootstrap";
import {
  EnvelopeFill,
  CheckCircle,
  ShieldLock,
  Lightning,
  Phone,
  Gear,
  Globe2,
  CloudCheck,
  ChevronRight,
} from "react-bootstrap-icons";

// ------------------------------------------------------------
// EasyEmailSetup.com – Landing Page
// Stack: React + TypeScript + react-bootstrap + react-bootstrap-icons
// Notes:
// - Keep Button href usage instead of `as={Nav.Link}` to avoid TS type errors.
// - All sections have ids for navbar anchor links.
// - Replace tel/mailto/links with your real URLs later.
// ------------------------------------------------------------

const brandColor = "#1b7cff"; // Easy, friendly blue

// Types
interface Feature {
  icon: JSX.Element;
  title: string;
  body: string;
}

interface Plan {
  name: string;
  price: string;
  note?: string;
  features: string[];
  ctaText: string;
  highlighted?: boolean;
}

interface FaqItem {
  q: string;
  a: string;
}

const FEATURES: Feature[] = [
  {
    icon: <EnvelopeFill size={28} />,
    title: "Your Domain, Your Brand",
    body: "We set up professional email like you@yourcompany.com on top providers (Google Workspace, Microsoft 365, or Zoho).",
  },
  {
    icon: <ShieldLock size={28} />,
    title: "Secure By Default",
    body: "Proper SPF, DKIM, DMARC, and phishing protections configured to keep your mail trusted and delivered.",
  },
  {
    icon: <Lightning size={28} />,
    title: "Fast Turnaround",
    body: "Typical setups completed in 1 business day with zero technical headaches for you.",
  },
  {
    icon: <Gear size={28} />,
    title: "Done‑For‑You Migration",
    body: "We migrate mailbox history and contacts wherever possible and ensure devices are connected.",
  },
  {
    icon: <Globe2 size={28} />,
    title: "All DNS Handled",
    body: "We manage DNS records (MX, SPF, DKIM, DMARC, MX priority) and verify everything end‑to‑end.",
  },
  {
    icon: <CloudCheck size={28} />,
    title: "Ongoing Support",
    body: "Optional support plans for new users, device setup, and troubleshooting as you grow.",
  },
];

const PLANS: Plan[] = [
  {
    name: "Starter",
    price: "$149 one‑time",
    note: "Best for solos",
    features: [
      "1 domain, up to 2 mailboxes",
      "Provider selection & setup",
      "DNS records (MX, SPF, DKIM)",
      "Basic security hardening",
      "Device setup guide",
    ],
    ctaText: "Get Started",
  },
  {
    name: "Business",
    price: "$299 one‑time",
    note: "Most popular",
    features: [
      "1 domain, up to 10 mailboxes",
      "Migration assist (up to 20GB)",
      "SPF, DKIM, DMARC + alignment",
      "Admin console walkthrough",
      "Deliverability checklist",
    ],
    ctaText: "Choose Business",
    highlighted: true,
  },
  {
    name: "Team",
    price: "From $599",
    note: "For growing orgs",
    features: [
      "Multi‑domain & aliases",
      "Bulk migration planning",
      "Advanced policies & groups",
      "Compliance guidance",
      "White‑glove device setup",
    ],
    ctaText: "Talk to Us",
  },
];

const FAQS: FaqItem[] = [
  {
    q: "Which provider should I choose?",
    a: "We’ll recommend Google Workspace, Microsoft 365, or Zoho based on your budget, existing tools, and security needs.",
  },
  {
    q: "Do you handle DNS and domain steps?",
    a: "Yes. We add and verify all necessary DNS records (MX, SPF, DKIM/DMARC) and confirm propagation.",
  },
  {
    q: "Can you migrate my old emails?",
    a: "For most providers we can migrate from Gmail, Outlook, IMAP, and more. Business plan includes up to ~20GB of mail.",
  },
  {
    q: "How long does setup take?",
    a: "Most single‑domain setups finish within 1 business day once we have admin access and DNS control.",
  },
  {
    q: "Is there ongoing support?",
    a: "Yes—pick a plan now or later. We offer add‑on help for new users, devices, groups, signatures, and policies.",
  },
];

const SectionHeader: React.FC<{ kicker?: string; title: string; subtitle?: string }> = ({
  kicker,
  title,
  subtitle,
}) => (
  <div className="text-center mb-4">
    {kicker && (
      <Badge bg="light" text="dark" className="mb-2">
        {kicker}
      </Badge>
    )}
    <h2 className="fw-bold">{title}</h2>
    {subtitle && <p className="text-muted lead mb-0">{subtitle}</p>}
  </div>
);

const FeatureCard: React.FC<Feature> = ({ icon, title, body }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <div className="mb-2" style={{ color: brandColor }}>{icon}</div>
      <Card.Title className="fw-semibold">{title}</Card.Title>
      <Card.Text className="text-muted">{body}</Card.Text>
    </Card.Body>
  </Card>
);

const PlanCard: React.FC<{ plan: Plan }> = ({ plan }) => (
  <Card
    className={`h-100 border-0 shadow-sm ${plan.highlighted ? "ring-2" : ""}`}
    style={plan.highlighted ? { outline: `3px solid ${brandColor}` } : {}}
  >
    <Card.Body className="d-flex flex-column">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Card.Title className="fw-bold mb-0">{plan.name}</Card.Title>
        {plan.note && <Badge bg="primary">{plan.note}</Badge>}
      </div>
      <div className="display-6 fw-bold mb-3">{plan.price}</div>
      <ListGroup variant="flush" className="mb-4">
        {plan.features.map((f, idx) => (
          <ListGroup.Item key={idx} className="border-0 px-0">
            <CheckCircle className="me-2" /> {f}
          </ListGroup.Item>
        ))}
      </ListGroup>
      <div className="mt-auto">
        <Button href="#contact" size="lg" className="w-100" style={{ backgroundColor: brandColor }}>
          {plan.ctaText} <ChevronRight className="ms-1" />
        </Button>
      </div>
    </Card.Body>
  </Card>
);

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} className="text-decoration-none text-muted me-3">
    {children}
  </a>
);

const EasyEmailSetupLanding_2025_11_07_chat: React.FC = () => {
  return (
    <>
      {/* NAVBAR */}
      <Navbar expand="lg" bg="white" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#home" className="fw-bold">
            <span style={{ color: brandColor }}>Easy</span>EmailSetup
            <Badge bg="light" text="dark" className="ms-2">.com</Badge>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto align-items-lg-center">
              <Nav.Link href="#features">Features</Nav.Link>
              <Nav.Link href="#process">Process</Nav.Link>
              <Nav.Link href="#pricing">Pricing</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Button href="#contact" className="ms-lg-3" style={{ backgroundColor: brandColor }}>
                Contact
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="py-5 bg-light">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={7}>
              <Badge bg="primary" className="mb-3">Done‑for‑you setup</Badge>
              <h1 className="fw-bold display-5 mb-3">
                Professional business email on <span style={{ color: brandColor }}>your domain</span>
              </h1>
              <p className="lead text-muted">
                We set up secure, reliable email like <strong>you@yourcompany.com</strong> on Google Workspace, Microsoft 365, or Zoho—
                fully configured for deliverability and security.
              </p>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button href="#pricing" size="lg" style={{ backgroundColor: brandColor }}>
                  View Plans
                </Button>
                <Button href="tel:+18005551234" size="lg" variant="outline-primary">
                  <Phone className="me-1" /> Call Now
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-3 mt-4 text-muted">
                <span><ShieldLock className="me-1" /> SPF/DKIM/DMARC</span>
                <span><CloudCheck className="me-1" /> Deliverability Check</span>
                <span><Lightning className="me-1" /> 1‑Day Typical Setup</span>
              </div>
            </Col>
            <Col md={5}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <EnvelopeFill className="me-2" size={22} style={{ color: brandColor }} />
                    <span className="fw-semibold">Get a quick quote</span>
                  </div>
                  <Form>
                    <Form.Group className="mb-3" controlId="qName">
                      <Form.Label>Full name</Form.Label>
                      <Form.Control type="text" placeholder="Jane Doe" />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="qDomain">
                      <Form.Label>Company domain</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>@</InputGroup.Text>
                        <Form.Control type="text" placeholder="yourcompany.com" />
                      </InputGroup>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="qEmail">
                      <Form.Label>Contact email</Form.Label>
                      <Form.Control type="email" placeholder="name@example.com" />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="qMailboxes">
                      <Form.Label>How many mailboxes?</Form.Label>
                      <Form.Select defaultValue="1-2">
                        <option>1-2</option>
                        <option>3-5</option>
                        <option>6-10</option>
                        <option>11-25</option>
                        <option>26+</option>
                      </Form.Select>
                    </Form.Group>
                    <div className="d-grid">
                      <Button href="#contact" size="lg" style={{ backgroundColor: brandColor }}>
                        Request Quote
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      By submitting, you agree to be contacted about email setup services.
                    </small>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-5">
        <Container>
          <SectionHeader
            kicker="Why choose us"
            title="Everything you need for bulletproof business email"
            subtitle="From provider selection to deliverability and security—we handle the details."
          />
          <Row className="g-4">
            {FEATURES.map((f, i) => (
              <Col md={6} lg={4} key={i}>
                <FeatureCard {...f} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-5 bg-light">
        <Container>
          <SectionHeader
            kicker="How it works"
            title="A simple, guided setup—done for you"
          />
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary" className="mb-2">Step 1</Badge>
                  <Card.Title className="fw-semibold">Choose a plan</Card.Title>
                  <Card.Text className="text-muted">
                    Pick the right plan based on your team size and needs (or ask us to recommend one).
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary" className="mb-2">Step 2</Badge>
                  <Card.Title className="fw-semibold">We configure everything</Card.Title>
                  <Card.Text className="text-muted">
                    Provider setup, DNS (MX/SPF/DKIM/DMARC), migration plan, and device instructions.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary" className="mb-2">Step 3</Badge>
                  <Card.Title className="fw-semibold">Go live confidently</Card.Title>
                  <Card.Text className="text-muted">
                    We test deliverability, review security, and walk admins through key actions.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-5">
        <Container>
          <SectionHeader
            kicker="Transparent pricing"
            title="Simple one‑time setup pricing"
            subtitle="Subscription fees for Google/Microsoft/Zoho are paid directly to the provider."
          />
          <Row className="g-4">
            {PLANS.map((p) => (
              <Col md={6} lg={4} key={p.name}>
                <PlanCard plan={p} />
              </Col>
            ))}
          </Row>
          <p className="text-center text-muted mt-3">
            Need something custom? <a href="#contact">Contact us</a> for a tailored quote.
          </p>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light">
        <Container>
          <SectionHeader kicker="FAQ" title="Answers to common questions" />
          <Accordion defaultActiveKey="0">
            {FAQS.map((f, i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{f.q}</Accordion.Header>
                <Accordion.Body>{f.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-5">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={6}>
              <SectionHeader
                kicker="Contact"
                title="Ready to look professional?"
                subtitle="Tell us about your domain and team—we’ll take it from there."
              />
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button href="mailto:hello@easyemailsetup.com" style={{ backgroundColor: brandColor }}>
                  <EnvelopeFill className="me-1" /> hello@easyemailsetup.com
                </Button>
                <Button href="tel:+18005551234" variant="outline-primary">
                  <Phone className="me-1" /> (800) 555‑1234
                </Button>
              </div>
              <p className="text-muted">
                Prefer a scheduled consult? Pick a time on our calendar and we’ll meet you on Zoom.
              </p>
              <Button href="#" variant="outline-secondary">
                Book a 15‑min consult
              </Button>
            </Col>
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Form>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="cName">
                          <Form.Label>Name</Form.Label>
                          <Form.Control type="text" placeholder="Jane Doe" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="cCompany">
                          <Form.Label>Company</Form.Label>
                          <Form.Control type="text" placeholder="Your Company LLC" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="cEmail">
                          <Form.Label>Email</Form.Label>
                          <Form.Control type="email" placeholder="you@domain.com" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="cPhone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control type="tel" placeholder="(555) 123‑4567" />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group controlId="cMessage">
                          <Form.Label>What do you need help with?</Form.Label>
                          <Form.Control as="textarea" rows={4} placeholder="e.g., Set up 6 mailboxes and migrate from Gmail; create group addresses; add DMARC." />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-grid mt-3">
                      <Button type="button" style={{ backgroundColor: brandColor }}>
                        Send Message
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      We’ll reply within one business day. No spam. Ever.
                    </small>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-4 border-top">
        <Container>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted">© {new Date().getFullYear()} EasyEmailSetup.com</div>
            <div>
              <FooterLink href="#">Privacy</FooterLink>
              <FooterLink href="#">Terms</FooterLink>
              <FooterLink href="#">Support</FooterLink>
            </div>
          </div>
        </Container>
      </footer>
    </>
  );
};

export default EasyEmailSetupLanding_2025_11_07_chat;
